import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';

const COLLECTION_NAME = 'userContractTemplates';

// Get shared contract template from templatesCollections
export const getSharedContractTemplate = async () => {
  try {
    const q = query(
      collection(db, 'templatesCollections'),
      where('title', '==', 'Contract Template')
    );
    const querySnapshot = await getDocs(q);
    
    console.log('Contract template query results:', querySnapshot.docs.length, 'documents found');
    
    if (!querySnapshot.empty) {
      const templateDoc = querySnapshot.docs[0];
      const templateData = templateDoc.data();
      
      console.log('Found contract template:', templateDoc.id, templateData.title);
      
      // Check if template already has contractElements (new format) or needs conversion (old format)
      let contractElements;
      if (templateData.contractElements && Array.isArray(templateData.contractElements)) {
        console.log('Template already in contractElements format, using directly');
        contractElements = templateData.contractElements;
      } else {
        console.log('Converting template from old format to contractElements format');
        contractElements = convertTemplateToContractElements(templateData);
      }
      
      return {
        id: templateDoc.id,
        templateName: templateData.title,
        contractElements: contractElements,
        isLocked: false, // Allow editing of shared template
        isShared: true,
        originalTemplate: templateData
      };
    } else {
      // Create a default contract template if none exists
      console.log('No contract template found, creating default template');
      return createDefaultContractTemplate();
    }
  } catch (error) {
    console.error('Error getting shared contract template:', error);
    // Return default template as fallback
    return createDefaultContractTemplate();
  }
};

// Create a default contract template when none exists
const createDefaultContractTemplate = () => {
  const defaultTemplate = {
    id: 'default-contract-template',
    templateName: 'Contract Template',
    contractElements: [
      {
        id: 'header-1',
        type: 'header',
        content: 'Service Agreement',
        style: { fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'intro-1',
        type: 'text',
        content: 'This Service Agreement ("Agreement") is entered into as of the date of signing by and between:',
        style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'service-provider-1',
        type: 'text',
        content: 'Builders Breakthrough Advisors, a limited liability corporation organized and existing under the laws of North Carolina, USA with its principal office located at 5102 Durham Chapel Hill Blvd Durham NC 27707 ("Service Provider"),',
        style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'client-1',
        type: 'text',
        content: '[Company Name], a corporation organized and existing under the laws of [State], USA with its principal office located at [Company Address], [City], [Zip Code] ("Client").',
        style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'section-header-1',
        type: 'header',
        content: 'Scope of Services',
        style: { fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', marginTop: '16px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'section-content-1',
        type: 'text',
        content: 'The Service Provider agrees to provide the following services to the Client: [Service Description]. The services shall be performed in accordance with the terms and conditions set forth in this Agreement.',
        style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'signature-section-1',
        type: 'header',
        content: 'Signatures',
        style: { fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', marginTop: '16px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'signature-field-1',
        type: 'signature',
        content: '[SIGNATURE]',
        style: { fontSize: '14px', borderBottom: '2px solid #000', paddingBottom: '4px', minWidth: '200px' },
        assignedTo: 'Client',
        subItems: []
      },
      {
        id: 'initial-field-1',
        type: 'initial',
        content: '[INITIALS]',
        style: { fontSize: '14px', borderBottom: '1px solid #000', paddingBottom: '2px', minWidth: '50px' },
        assignedTo: 'Client',
        subItems: []
      }
    ],
    isLocked: true,
    isShared: true,
    originalTemplate: {
      title: 'Contract Template',
      content: {
        formFields: [
          { id: 'companyName', label: 'Company Name', placeholder: 'Your Company Name', variable: '[Company Name]' },
          { id: 'state', label: 'State of Incorporation', placeholder: 'State', variable: '[State]' },
          { id: 'companyAddress', label: 'Street Address', placeholder: 'Street address', variable: '[Company Address]' },
          { id: 'city', label: 'City', placeholder: 'City', variable: '[City]' },
          { id: 'zipCode', label: 'ZIP Code', placeholder: 'ZIP code', variable: '[Zip Code]' },
          { id: 'serviceDescription', label: 'Service Description', placeholder: 'Description of services', variable: '[Service Description]' },
          { id: 'signerName', label: 'Signatory Full Name', placeholder: 'Full legal name', variable: '[Owner Name]' },
          { id: 'signerTitle', label: 'Title', placeholder: 'e.g. Owner', variable: '[Owner Title]' },
          { id: 'signature', label: 'Electronic Signature', placeholder: 'Type your full legal name as signature', variable: '[Signature]' }
        ]
      }
    }
  };
  
  console.log('Created default contract template');
  return defaultTemplate;
};

// Convert template from templatesCollections format to contractElements format
const convertTemplateToContractElements = (templateData) => {
  const elements = [];
  let elementId = 1;
  
  // Check if this is already in contractElements format (from shared template)
  if (templateData.contractElements && Array.isArray(templateData.contractElements)) {
    console.log('Template already in contractElements format, returning as-is');
    return templateData.contractElements;
  }
  
  // Add header
  elements.push({
    id: `header-${elementId++}`,
    type: 'header',
    content: templateData.title,
    style: { fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' },
    assignedTo: null,
    subItems: []
  });
  
  // Add intro text
  if (templateData.content?.intro) {
    elements.push({
      id: `intro-${elementId++}`,
      type: 'text',
      content: templateData.content.intro,
      style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
      assignedTo: null,
      subItems: []
    });
  }
  
  // Add service provider
  if (templateData.content?.serviceProvider) {
    elements.push({
      id: `service-provider-${elementId++}`,
      type: 'text',
      content: templateData.content.serviceProvider,
      style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
      assignedTo: null,
      subItems: []
    });
  }
  
  // Add client with variables
  if (templateData.content?.client) {
    elements.push({
      id: `client-${elementId++}`,
      type: 'text',
      content: templateData.content.client,
      style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
      assignedTo: null,
      subItems: []
    });
  }
  
  // Add sections
  if (templateData.content?.sections) {
    templateData.content.sections.forEach((section, index) => {
      // Add section header
      elements.push({
        id: `section-header-${elementId++}`,
        type: 'header',
        content: section.title,
        style: { fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', marginTop: '16px' },
        assignedTo: null,
        subItems: []
      });
      
      // Add section content
      elements.push({
        id: `section-content-${elementId++}`,
        type: 'text',
        content: section.content,
        style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
        assignedTo: null,
        subItems: []
      });
    });
  }
  
  // Only add signature section if it doesn't already exist in the template
  // This allows users to delete signature elements if they want to
  const hasSignatureSection = templateData.content?.sections?.some(section => 
    section.title === 'Signatures' || section.content?.includes('Signatures')
  );
  
  if (!hasSignatureSection) {
    // Add signature section
    elements.push({
      id: `signature-section-${elementId++}`,
      type: 'header',
      content: 'Signatures',
      style: { fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', marginTop: '16px' },
      assignedTo: null,
      subItems: []
    });
    
    // Add signature fields
    elements.push({
      id: `signature-field-${elementId++}`,
      type: 'signature',
      content: '[SIGNATURE]',
      style: { fontSize: '14px', borderBottom: '2px solid #000', paddingBottom: '4px', minWidth: '200px' },
      assignedTo: 'Client',
      subItems: []
    });
    
    elements.push({
      id: `initial-field-${elementId++}`,
      type: 'initial',
      content: '[INITIALS]',
      style: { fontSize: '14px', borderBottom: '1px solid #000', paddingBottom: '2px', minWidth: '50px' },
      assignedTo: 'Client',
      subItems: []
    });
  }
  
  return elements;
};

// Get contract template for a specific user (now returns shared template)
export const getUserContractTemplate = async (userId) => {
  try {
    // Always return the shared template
    return await getSharedContractTemplate();
  } catch (error) {
    console.error('Error getting user contract template:', error);
    throw error;
  }
};

// Save shared contract template to templatesCollections
export const saveSharedContractTemplate = async (templateData) => {
  try {
    // First, try to find existing template
    const q = query(
      collection(db, 'templatesCollections'),
      where('title', '==', 'Contract Template')
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Update existing template
      const templateDoc = querySnapshot.docs[0];
      const docRef = doc(db, 'templatesCollections', templateDoc.id);
      
      const updatedData = {
        ...templateDoc.data(),
        ...templateData,
        updatedAt: serverTimestamp()
      };
      
      await setDoc(docRef, updatedData, { merge: true });
      console.log('Updated shared contract template:', templateDoc.id);
      return { id: templateDoc.id, ...updatedData };
    } else {
      // Create new template
      const newTemplateData = {
        title: 'Contract Template',
        description: 'Standard contract template for service agreements between Builders Breakthrough Advisors and clients.',
        pillar: 'General',
        type: 'DOC',
        file: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        unlocked: true,
        category: "Collections",
        ...templateData
      };
      
      const docRef = await addDoc(collection(db, 'templatesCollections'), newTemplateData);
      console.log('Created new shared contract template:', docRef.id);
      return { id: docRef.id, ...newTemplateData };
    }
  } catch (error) {
    console.error('Error saving shared contract template:', error);
    throw error;
  }
};

// Update shared contract template elements
export const updateSharedContractElements = async (contractElements) => {
  try {
    console.log('updateSharedContractElements called with:', contractElements.length, 'elements');
    
    // Save the template directly in contractElements format (new format)
    const templateData = {
      title: 'Contract Template',
      description: 'Standard contract template for service agreements between Builders Breakthrough Advisors and clients.',
      pillar: 'General',
      contractElements: contractElements, // Save in new format
      updatedAt: new Date().toISOString()
    };
    
    console.log('Saving template in contractElements format:', templateData);
    
    // Save to templatesCollections
    const result = await saveSharedContractTemplate(templateData);
    console.log('saveSharedContractTemplate result:', result);
    return result;
  } catch (error) {
    console.error('Error updating shared contract elements:', error);
    throw error;
  }
};

// Save or update contract template for a user
export const saveUserContractTemplate = async (userId, templateData) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const dataToSave = {
      ...templateData,
      userId: userId,
      updatedAt: serverTimestamp()
    };
    
    await setDoc(docRef, dataToSave, { merge: true });
    return dataToSave;
  } catch (error) {
    console.error('Error saving user contract template:', error);
    throw error;
  }
};

// Update contract template with specific fields
export const updateContractTemplate = async (userId, updates) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const dataToUpdate = {
      ...updates,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(docRef, dataToUpdate);
    return dataToUpdate;
  } catch (error) {
    console.error('Error updating contract template:', error);
    throw error;
  }
};

// Update contract elements array
export const updateContractElements = async (userId, contractElements) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(docRef, {
      contractElements: contractElements,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating contract elements:', error);
    throw error;
  }
};

// Update template lock status
export const updateTemplateLockStatus = async (userId, isLocked) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(docRef, {
      isLocked: isLocked,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating template lock status:', error);
    throw error;
  }
};

// Update template name
export const updateTemplateName = async (userId, templateName) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(docRef, {
      templateName: templateName,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating template name:', error);
    throw error;
  }
};

// Delete user contract template
export const deleteUserContractTemplate = async (userId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting user contract template:', error);
    throw error;
  }
};

// Get all contract templates (admin function)
export const getAllContractTemplates = async () => {
  try {
    const templatesRef = collection(db, COLLECTION_NAME);
    const querySnapshot = await getDocs(templatesRef);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error getting all contract templates:', error);
    throw error;
  }
};
